import type React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormGroup,
  Checkbox,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import { BookingData } from "../../../../../types/bookings";
import {
  AdditionalServiceItem,
  AdditionalServicesCategories,
  Package,
} from "../../../../../types/package";
import apiService from "../../../../../service/Api";

// import type { BookingData } from "../../../types/bookings";
// import type {
//   Package,
//   AdditionalServicesCategories,
//   AdditionalServiceItem,
// } from "../../../types/package";
// import apiService from "../../../service/Api";

// ── API response types (from AddBookingPackageSelection) ─────────────────────

interface ServiceResponse {
  id: number;
  name: string;
  description: string | null;
  price: string | null;
  service_category: string;
  service_category_display: string;
  created_at: string;
  updated_at: string;
}

interface PaginatedServiceResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ServiceResponse[];
}

interface PackageBaseService {
  id: number;
  name: string;
  price?: string | number;
}

interface PaginatedPackageResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    id: number;
    name: string;
    package_type: string;
    base_price: string;
    description: string;
    created_at: string;
    updated_at: string;
    company: number;
    base_services: PackageBaseService[];
  }[];
}

// ── Type guards (from AddBookingPackageSelection) ─────────────────────────────

const isPaginatedPackageResponse = (
  data: unknown,
): data is PaginatedPackageResponse =>
  typeof data === "object" &&
  data !== null &&
  "results" in data &&
  Array.isArray((data as PaginatedPackageResponse).results) &&
  (data as PaginatedPackageResponse).results.every(
    (item) =>
      typeof item === "object" &&
      item !== null &&
      "id" in item &&
      "name" in item &&
      "base_price" in item &&
      "base_services" in item,
  );

const isPaginatedServiceResponse = (
  data: unknown,
): data is PaginatedServiceResponse =>
  typeof data === "object" &&
  data !== null &&
  "results" in data &&
  Array.isArray((data as PaginatedServiceResponse).results) &&
  (data as PaginatedServiceResponse).results.every(
    (item) =>
      typeof item === "object" &&
      item !== null &&
      "id" in item &&
      "name" in item &&
      "service_category" in item,
  );

// ── Props ────────────────────────────────────────────────────────────────────

interface Props {
  bookingData: BookingData;
  setBookingData: React.Dispatch<React.SetStateAction<BookingData>>;
  serviceType: string;
}

// ── Palette for package cards (cycles through a small set of accent colours) ─

const CARD_COLORS = ["#8B5A2B", "#2B6CB0", "#276749", "#744210", "#553C9A"];

// ── Add this helper near the top of the file, above the component ─────────

const parseHtmlToLines = (html: string): string[] => {
  const withNewlines = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<\/div>/gi, "\n");

  return withNewlines
    .replace(/<[^>]+>/g, "") // strip remaining tags
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
};

export function PackageSelection({
  bookingData,
  setBookingData,
  serviceType,
}: Props) {
  const [packages, setPackages] = useState<Package[]>([]);
  const [additionalServicesConfig, setAdditionalServicesConfig] =
    useState<AdditionalServicesCategories>({});
  const [editedTotal, setEditedTotal] = useState<number | string>(
    bookingData.total || 0,
  );
  const [loading, setLoading] = useState(true);
  const [isManualEdit, setIsManualEdit] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const isInitialMount = useRef(true);
  const lastCalculatedTotal = useRef<number>(0);
  const currentPackageIdRef = useRef(bookingData.package?.id);

  // ── Totals calculator (from AddBookingPackageSelection) ───────────────────
  const calculateTotals = useCallback(
    (data: BookingData) => {
      const baseServicesTotal =
        data.package?.base_services?.reduce(
          (sum, s) => sum + Number(s.price || 0),
          0,
        ) ?? 0;

      const packageTotal = (data.package?.price || 0) + baseServicesTotal;
      let additionalTotal = 0;
      const allAdditionalServices: AdditionalServiceItem[] = [];

      Object.keys(additionalServicesConfig).forEach((category) => {
        const selected = data[category];
        if (Array.isArray(selected)) {
          selected.forEach((val) => {
            const service = additionalServicesConfig[category]?.find(
              (s) => s.value === val,
            );
            if (service) {
              additionalTotal += service.price;
              allAdditionalServices.push({ ...service, category });
            }
          });
        }
      });

      return {
        packageTotal,
        additionalTotal,
        finalTotal: parseFloat((packageTotal + additionalTotal).toFixed(2)),
        allAdditionalServices,
      };
    },
    [additionalServicesConfig],
  );

  // ── Auto-recalculate totals when selections change ────────────────────────
  useEffect(() => {
    if (isManualEdit || Object.keys(additionalServicesConfig).length === 0)
      return;
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const { packageTotal, additionalTotal, finalTotal, allAdditionalServices } =
      calculateTotals(bookingData);

    if (Math.abs(lastCalculatedTotal.current - finalTotal) > 0.01) {
      lastCalculatedTotal.current = finalTotal;
      setEditedTotal(finalTotal);

      const id = setTimeout(() => {
        setBookingData((prev) => {
          if (
            Math.abs((prev.total || 0) - finalTotal) > 0.01 ||
            Math.abs((prev.package_total || 0) - packageTotal) > 0.01 ||
            Math.abs((prev.additional_services_total || 0) - additionalTotal) >
              0.01
          ) {
            return {
              ...prev,
              total: finalTotal,
              package_total: packageTotal,
              additional_services_total: additionalTotal,
              additional_services: allAdditionalServices,
            };
          }
          return prev;
        });
      }, 10);

      return () => clearTimeout(id);
    }
  }, [
    bookingData,
    additionalServicesConfig,
    calculateTotals,
    isManualEdit,
    setBookingData,
  ]);

  // ── Fetch packages + additional services (from AddBookingPackageSelection) ─
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const serviceTypeMap: Record<string, string | string[]> = {
          FUNERAL: ["Funeral"],
          FUNERAL_HOME: "FuneralHome",
          CHURCH: ["Church"],
          CONFERENCE: "Conference",
          EVENT: "Event",
          OTHER: "Other",
        };

        const selectedType = serviceTypeMap[serviceType] ?? "Other";

        // ── Paginated package fetch helper ─────────────────────────────────
        const fetchAllPackages = async (url: string) => {
          let results: PaginatedPackageResponse["results"] = [];
          let nextUrl: string | null = url;
          while (nextUrl) {
            const { data }: { data: unknown } = await apiService.get(nextUrl);
            if (!isPaginatedPackageResponse(data))
              throw new Error("Invalid package data format");
            results = [...results, ...data.results];
            nextUrl = data.next
              ? new URL(data.next).pathname + new URL(data.next).search
              : null;
          }
          return results;
        };

        // ── Fetch & map packages ───────────────────────────────────────────
        const rawItems = Array.isArray(selectedType)
          ? (
              await Promise.all(
                selectedType.map((t) =>
                  fetchAllPackages(
                    `/api/packages/package/?package_type=${t}&page_size=20`,
                  ),
                ),
              )
            ).flat()
          : await fetchAllPackages(
              `/api/packages/package/?package_type=${selectedType}&page_size=20`,
            );

        const fetchedPackages: Package[] = rawItems.map((item) => ({
          id: item.id,
          name: item.name,
          price: parseFloat(item.base_price) || 0,
          contents: item.description ? parseHtmlToLines(item.description) : [],

          package_type: item.package_type,
          base_services: item.base_services,
        }));

        setPackages(fetchedPackages);

        // ── Default to first package if none selected ──────────────────────
        if (fetchedPackages.length > 0 && !currentPackageIdRef.current) {
          const def = fetchedPackages[0];
          lastCalculatedTotal.current = def.price;
          setEditedTotal(def.price);
          setBookingData((prev) => ({
            ...prev,
            package: { ...def },
            package_total: def.price,
            additional_services_total: 0,
            total: def.price,
          }));
        }

        // ── Fetch additional services ──────────────────────────────────────
        let allServices: ServiceResponse[] = [];
        let nextUrl: string | null = "/api/packages/package-service/";

        while (nextUrl) {
          const { data }: { data: unknown } = await apiService.get(nextUrl);
          if (!isPaginatedServiceResponse(data))
            throw new Error("Invalid service data format");
          allServices = [...allServices, ...data.results];
          nextUrl = data.next
            ? new URL(data.next).pathname + new URL(data.next).search
            : null;
        }

        const grouped: AdditionalServicesCategories = allServices.reduce(
          (acc, item) => {
            const cat = item.service_category || "Other";
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push({
              value: item.id.toString(),
              label: item.name,
              price: parseFloat(item.price ?? "0") || 0,
              description: item.description || undefined,
            });
            return acc;
          },
          {} as AdditionalServicesCategories,
        );

        setAdditionalServicesConfig(grouped);
      } catch (error: unknown) {
        console.error("Error fetching packages/services:", error);
        setSnackbar({
          open: true,
          message:
            (error as { message?: string })?.message ??
            "Failed to load packages",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [serviceType, setBookingData]);

  // ── Keep ref in sync ──────────────────────────────────────────────────────
  useEffect(() => {
    currentPackageIdRef.current = bookingData.package?.id;
  }, [bookingData.package?.id]);

  useEffect(() => {
    setEditedTotal(parseFloat(String(bookingData.total)) || 0);
  }, [bookingData.total]);

  // ── Handlers (from AddBookingPackageSelection) ────────────────────────────
  const handlePackageSelect = useCallback(
    (pkg: Package) => {
      setIsManualEdit(false);
      setBookingData((prev) => ({ ...prev, package: { ...pkg } }));
    },
    [setBookingData],
  );

  const handleAdditionalServiceChange = useCallback(
    (category: string, value: string, checked: boolean) => {
      setIsManualEdit(false);
      setBookingData((prev) => {
        const updated: BookingData = { ...prev };
        updated[category] = checked
          ? [...((updated[category] as string[]) || []), value]
          : ((updated[category] as string[]) || []).filter((v) => v !== value);
        return updated;
      });
    },
    [setBookingData],
  );

  const handleTotalChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsManualEdit(true);
      const value = e.target.value.replace(/[^0-9.]/g, "");
      const newTotal = parseFloat(value) || 0;
      setEditedTotal(value);
      lastCalculatedTotal.current = newTotal;
      setBookingData((prev) => ({ ...prev, total: newTotal }));
    },
    [setBookingData],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box py={4} textAlign="center">
        <Typography>Loading packages and services…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} mb={3}>
        Select Package
      </Typography>

      {/* ── Package cards (Card UI from old modal, data from API) ────────── */}
      {packages.length === 0 ? (
        <Typography color="text.secondary">No packages available.</Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 2,
          }}
        >
          {packages.map((pkg, i) => {
            const color = CARD_COLORS[i % CARD_COLORS.length];
            const isSelected = bookingData.package?.id === pkg.id;

            return (
              <Card
                key={pkg.id}
                onClick={() => handlePackageSelect(pkg)}
                sx={{
                  cursor: "pointer",
                  position: "relative",
                  border: isSelected
                    ? `2px solid ${color}`
                    : "1px solid #e0e0e0",
                  borderRadius: 3,
                  transition: "all 0.2s",
                  "&:hover": { boxShadow: 3 },
                }}
              >
                {i === 0 && (
                  <Chip
                    label="Popular"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      bgcolor: "#ff5722",
                      color: "#fff",
                      fontSize: 10,
                    }}
                  />
                )}
                <CardContent>
                  <Typography variant="h6" fontWeight={600}>
                    {pkg.name}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color={color}>
                    ${pkg.price.toFixed(2)}
                  </Typography>

                  {/* Package contents */}
                  {pkg.contents && pkg.contents.length > 0 && (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={1}
                      >
                        Features:
                      </Typography>
                      {pkg.contents.slice(0, 4).map((f, idx) => (
                        <Typography key={idx} variant="body2">
                          • {f}
                        </Typography>
                      ))}
                    </>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    size="small"
                    sx={{
                      mt: 2,
                      bgcolor: color,
                      textTransform: "none",
                      borderRadius: 4,
                      "&:hover": { filter: "brightness(0.9)" },
                    }}
                  >
                    {isSelected ? "Selected" : "Select Package"}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* ── Additional services (from AddBookingPackageSelection) ─────────── */}
      {Object.keys(additionalServicesConfig).length > 0 && (
        <Box mt={4}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Additional Services
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            {Object.entries(additionalServicesConfig).map(
              ([category, services]) => (
                <Box
                  key={category}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <FormControl fullWidth>
                    <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
                      {category}
                    </FormLabel>
                    <FormGroup>
                      {services.map(({ value, label, price }) => (
                        <Box
                          key={`${category}-${value}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  (bookingData[category] as string[])?.includes(
                                    value,
                                  ) ?? false
                                }
                                onChange={(e) =>
                                  handleAdditionalServiceChange(
                                    category,
                                    value,
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={label}
                            sx={{ flexGrow: 1 }}
                          />
                          <Typography variant="body2">
                            ${price.toFixed(2)}
                          </Typography>
                        </Box>
                      ))}
                    </FormGroup>
                  </FormControl>
                </Box>
              ),
            )}
          </Box>
        </Box>
      )}

      {/* ── Totals summary ────────────────────────────────────────────────── */}
      <Box
        sx={{
          mt: 4,
          p: 3,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6">
            Total{" "}
            <Typography component="span" variant="caption">
              (including additional services)
            </Typography>
          </Typography>

          <TextField
            type="text"
            value={`${editedTotal}`}
            onChange={handleTotalChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
            sx={{ mt: 1, width: 140 }}
          />

          <Box mt={1}>
            <Typography variant="body2" color="text.secondary">
              Package: ${(bookingData.package_total || 0).toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Additional Services: $
              {(bookingData.additional_services_total || 0).toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Error snackbar ────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
